import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  Scale,
  ListChecks,
  Home,
  Building2,
  Palette,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Chambourcy ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison rapide de cartons de déménagement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que le temps s&apos;accélère à l&apos;approche du jour J,
              notre service logistique de proximité intervient directement à
              votre adresse pour vous ravitailler sans délai.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale réactive",
                body: "Que vous habitiez dans le centre-ville historique ou à proximité des grands axes de Chambourcy, nous livrons vos cartons à plat, propres et parfaitement secs.",
                Icon: Calendar,
              },
              {
                title: "Zéro déplacement fastidieux",
                body: "Plus besoin de perdre du temps dans les files d'attente ou de surcharger le coffre de votre voiture dans les zones commerciales des Yvelines. Vous planifiez la réception selon vos disponibilités pour commencer à emballer vos pièces immédiatement.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Comment optimiser le rangement de ses cartons ? ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode pro
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment optimiser le rangement de ses cartons ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Remplir méthodiquement ses cartons est le meilleur moyen de
            gagner de la place dans le camion et de faciliter le déballage
            dans votre futur logement.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Répartition stratégique du poids",
              body: "Placez toujours les objets les plus lourds et denses (comme les livres ou les plats) au fond du carton, et réservez le haut pour les éléments plus légers.",
              Icon: Scale,
            },
            {
              title: "Combler les espaces vides",
              body: "Un carton à moitié vide risque de s'affaisser lors de l'empilement. Utilisez notre film à bulles ou du linge de maison pour caler le contenu afin que rien ne bouge pendant le transport.",
              Icon: Palette,
            },
            {
              title: "Le marquage systématique",
              body: "Indiquez sur le dessus et les côtés de chaque boîte la pièce de destination (Cuisine, Salon, Chambre 1) et une brève description du contenu. Cela fera gagner un temps précieux aux déménageurs.",
              Icon: ListChecks,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chambourcy-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Marqueurs &amp; adhésifs pro
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chambourcy-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits complets
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Accompagnement pour tous projets ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Particuliers &amp; entreprises
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Un accompagnement pour tous vos projets de déménagement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque changement d&apos;adresse est unique, et notre rôle va
            bien au-delà de la simple fourniture de boîtes en carton ondulé.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Particuliers / Familles */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Home className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Pour les particuliers et les familles
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nous vous guidons à travers des packs de cartons clés en main
              (du Studio à la grande Maison) calibrés par des experts pour
              correspondre précisément à la taille de votre foyer.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              Plus besoin de calculer ou de deviner les quantités : nos
              packs intègrent le bon ratio de petits, moyens et grands
              cartons, complétés par les accessoires de protection et de
              fermeture indispensables. Vous gagnez du temps, et vous
              évitez les allers-retours pour acheter du matériel
              supplémentaire à la dernière minute.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Packs Studio, T1, T2, T3 &amp; Maison
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Ratio calibré par des experts
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Accessoires de protection inclus
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Conseils personnalisés
              </div>
            </div>
          </div>

          {/* Entreprises / Bureaux */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Building2 className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Pour les entreprises et les bureaux
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Nous proposons des solutions adaptées aux professionnels de
              Chambourcy, incluant des cartons d&apos;archivage renforcés
              et du matériel de protection haute densité pour sécuriser les
              parcs informatiques.
            </p>
            <p className="mt-3 text-[14.5px] text-[#3a4856] leading-relaxed">
              En choisissant nos services, vous bénéficiez d&apos;un
              véritable partenaire logistique à vos côtés dans le 78. De la
              commande en ligne de vos équipements jusqu&apos;à la
              livraison sur le pas de votre porte, nous mettons tout en
              œuvre pour que votre déménagement se transforme en une
              expérience fluide, efficace et sans le moindre stress.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons d&apos;archivage renforcés
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Protection haute densité pour IT
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Devis sur-mesure pour les volumes pro
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Livraison planifiée selon vos contraintes
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chambourcy-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chambourcy-cta-manutention"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Diables &amp; manutention
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chambourcy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
